/* The project document: what the customer is building. Stored as JSON
   (projects.doc), validated on both sides with these schemas. All sizes are
   millimetres. Nothing here is a flattened image: every item points at its
   design, and every design at the untouched uploaded original. */

import { z } from 'zod';
import { TRANSFERS } from './config.js';
import type { InkMask } from './ink.js';

export const DOC_VERSION = 1;

const Id = z.string().min(1).max(64);
const Mm = z.number().finite();

export const SheetChoiceSchema = z.discriminatedUnion('mode', [
  /** pick the cheapest sheet that fits (fixed sizes or by the metre) */
  z.object({ mode: z.literal('auto') }),
  /** one of the fixed sizes (A4, A3, half metre …) */
  z.object({ mode: z.literal('fixed'), key: z.string() }),
  /** by the metre at a set length (1 m, 2 m, custom) */
  z.object({ mode: z.literal('length'), lengthMm: z.number().positive() }),
  /** by the metre, as long as the content needs (rolling canvas) */
  z.object({ mode: z.literal('roll') })
]);
export type SheetChoice = z.infer<typeof SheetChoiceSchema>;

const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/** The settings behind a text design (Add text), saved with the gangsheet so it can be edited again
    on any device. The printed artwork itself is the SVG made from them (the design's asset). */
export const TextSpecSchema = z.object({
  text: z.string().max(600),
  font: z.string().regex(/^[a-z0-9-]{1,40}$/),
  color: Hex,
  align: z.enum(['left', 'center', 'right']),
  /** extra space between letters, % of the font size */
  letterSpacing: z.number().min(-50).max(200),
  /** line height, × the font size */
  lineHeight: z.number().min(0.5).max(4),
  outline: z.boolean(),
  outlineColor: Hex,
  /** outline thickness, % of the font size */
  outlineWidth: z.number().min(0).max(50),
  /** printed width of the whole text, mm */
  widthMm: z.number().min(1).max(10000)
});
export type TextSpec = z.infer<typeof TextSpecSchema>;

export const DesignSchema = z.object({
  id: Id,
  assetId: Id,
  name: z.string().max(200),
  widthMm: Mm.positive(),
  heightMm: Mm.positive(),
  quantity: z.number().int().min(0).max(10000),
  lockRotation: z.boolean(),
  /** use the artwork's real (non-transparent) bounds; off = the whole canvas */
  trim: z.boolean(),
  presetKey: z.string().max(40).optional(),
  /** a text design's editable settings (0.3d); absent for uploaded artwork */
  text: TextSpecSchema.optional()
});
export type Design = z.infer<typeof DesignSchema>;

export const ItemSchema = z.object({
  id: Id,
  designId: Id,
  /** centre of the artwork on the sheet, mm from the sheet's top-left */
  cx: Mm,
  cy: Mm,
  /** unrotated print size of this copy */
  widthMm: Mm.positive(),
  heightMm: Mm.positive(),
  /** degrees, clockwise */
  rotation: Mm,
  flipX: z.boolean(),
  flipY: z.boolean(),
  locked: z.boolean()
});
export type Item = z.infer<typeof ItemSchema>;

export const SheetSchema = z.object({
  id: Id,
  kind: z.enum(['fixed', 'length', 'roll']),
  fixedKey: z.string().optional(),
  widthMm: Mm.positive(),
  lengthMm: Mm.positive(),
  /** back to front */
  items: z.array(ItemSchema).max(20000)
});
export type Sheet = z.infer<typeof SheetSchema>;

export const ProjectSettingsSchema = z.object({
  sheet: SheetChoiceSchema,
  gapMm: z.number().min(0).max(100),
  allowRotation: z.boolean(),
  unit: z.enum(['mm', 'cm', 'in'])
});
export type ProjectSettings = z.infer<typeof ProjectSettingsSchema>;

export const ProjectDocSchema = z.object({
  v: z.literal(DOC_VERSION),
  name: z.string().max(120),
  transfer: z.enum(TRANSFERS),
  settings: ProjectSettingsSchema,
  designs: z.array(DesignSchema).max(1000),
  sheets: z.array(SheetSchema).max(200),
  /** the design list changed since the sheets were built */
  layoutStale: z.boolean(),
  /** warnings the customer accepted: 'dpi:<designId>' / 'opaque:<designId>' */
  acknowledged: z.array(Id).max(1000)
});
export type ProjectDoc = z.infer<typeof ProjectDocSchema>;

/** What the browser knows about an uploaded asset (from GET /api/assets). */
export interface AssetInfo {
  id: string;
  status: 'uploading' | 'processing' | 'ready' | 'failed';
  kind: 'raster' | 'vector' | 'pdf';
  name: string;
  mime: string;
  bytes: number;
  /** source pixel size (vectors: the size they were analysed at) */
  widthPx: number;
  heightPx: number;
  hasAlpha: boolean;
  /** no transparent pixels at all: prints as a full rectangle */
  opaque: boolean;
  /** trimmed (non-transparent) box as fractions of the source, 0..1 */
  trim: { x: number; y: number; w: number; h: number };
  /** vectors: natural size in mm if the file says so */
  naturalMm?: { w: number; h: number } | null;
  previewUrl?: string;
  thumbUrl?: string;
  error?: string | null;
  pages?: number;
  /** where the artwork actually prints (see ink.ts); null/absent = its whole rectangle */
  ink?: InkMask | null;
}

export function newId(prefix = ''): string {
  const c: Crypto | undefined = (globalThis as { crypto?: Crypto }).crypto;
  const u = c && typeof c.randomUUID === 'function'
    ? c.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
        const r = (Math.random() * 16) | 0;
        return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
  return prefix + u.replace(/-/g, '').slice(0, 16);
}

/** The gap a layout keeps: the customer's choice, never under the shop's minimum (or the shop's gap when
    customers can't change it), and never under the printed-artwork minimum for this transfer type. */
export function layoutGap(
  doc: { settings: { gapMm: number }; transfer?: 'dtf' | 'uv' },
  b: { gapMm?: number; minGapMm?: number; allowCustomerGap?: boolean; inkGapMm?: { dtf: number; uv: number } }
): number {
  const ink = inkGapFor(doc, b);
  if (b.allowCustomerGap === false && typeof b.gapMm === 'number') return Math.max(b.gapMm, ink);
  return Math.max(doc.settings.gapMm, b.minGapMm ?? 0, ink);
}

/** The smallest gap allowed between printed artwork (ink to ink) for this gangsheet's transfer type. */
export function inkGapFor(doc: { transfer?: 'dtf' | 'uv' }, b: { inkGapMm?: { dtf: number; uv: number }; minGapMm?: number }): number {
  const v = b.inkGapMm?.[doc.transfer || 'dtf'];
  return typeof v === 'number' ? v : (b.minGapMm ?? 0);
}

export function emptyProject(transfer: 'dtf' | 'uv', gapMm: number): ProjectDoc {
  return {
    v: DOC_VERSION,
    name: 'Untitled gangsheet',
    transfer,
    settings: { sheet: { mode: 'auto' }, gapMm, allowRotation: true, unit: 'cm' },
    designs: [],
    sheets: [],
    layoutStale: false,
    acknowledged: []
  };
}

/** A gangsheet the customer lays out by hand (Start new gangsheet, or "By the metre" chosen): one or more
    by-the-metre sheets that grow with what's on them. Changes in the design list go straight onto the
    sheet (new artwork is placed, quantities add or remove copies, sizes resize the copies), instead of
    asking for a rebuild, and Auto Build never swaps the sheet for another format by itself. */
export function isManualDoc(doc: Pick<ProjectDoc, 'settings' | 'sheets' | 'layoutStale'>): boolean {
  // not while a format change is waiting for its rebuild, and only on by-the-metre sheets
  return doc.settings.sheet.mode === 'roll' && doc.sheets.length > 0 && !doc.layoutStale && doc.sheets.every(s => s.kind === 'roll');
}

/** A blank by-the-metre sheet at its shortest length (58 × 100 cm with the Printees settings). */
export function blankRollSheet(byLength: { widthMm: number; minLengthMm: number }): Sheet {
  return { id: newId('s'), kind: 'roll', widthMm: byLength.widthMm, lengthMm: byLength.minLengthMm, items: [] };
}

/** Number of transfers the design list asks for. */
export function requestedCount(doc: Pick<ProjectDoc, 'designs'>): number {
  return doc.designs.reduce((n, d) => n + d.quantity, 0);
}

/** Copies of each design currently placed on the sheets. */
export function placedCounts(doc: Pick<ProjectDoc, 'sheets'>): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of doc.sheets) for (const it of s.items) m.set(it.designId, (m.get(it.designId) || 0) + 1);
  return m;
}
